import React, { useState, useEffect, useRef } from "react";
import chroma from "chroma-js";
import MuiIconButton from "@material-ui/core/IconButton";
import withStyles from "../../styles/withStyles";
import lightPalette from "../../styles/colors/light";
import darkPalette from "../../styles/colors/dark";
import PropTypes from "prop-types";

function IconButton({
  classes,
  className,
  green,
  light,
  onClick,
  small,
  ...props
}) {
  let [active, setActive] = useState(false);
  let timeoutIds = useRef([]);
  let color = "dark";
  if (light) color = "light";
  if (green) color = "green";

  let registerTimeout = (f, ms) => {
    let timeoutId = setTimeout(f, ms);
    timeoutIds.current.push(timeoutId);
  };

  let handleClick = e => {
    e.persist();
    setActive(true);
    if (typeof onClick === "function") onClick(e);
  };

  let cleanup = () => {
    timeoutIds.current.forEach(clearTimeout);
  };

  useEffect(() => {
    if (active === true) {
      registerTimeout(() => setActive(false), 250);
    }
    return cleanup;
  });

  return (
    <MuiIconButton
      className={`${active ? "Mui-active" : ""} ${
        small ? classes.small : ""
      } ${className}`}
      classes={{
        root: classes[`${color}Button`],
        disabled: classes.disabled
      }}
      onClick={handleClick}
      {...props}
    />
  );
}

let styles = theme => ({
  darkButton: {
    "& svg": {
      fill: darkPalette.action.active
    },
    padding: "8px",
    transition: `background-color ${theme.transitions.common.short},
    box-shadow ${theme.transitions.common.short}`,
    "&:focus, &:hover": {
      backgroundColor: darkPalette.action.hover
    },
    "&:active, &.Mui-active": {
      backgroundColor: darkPalette.action.selected,
      boxShadow: `0 0 0 0.16rem rgba(0, 0, 0, 0.2)`
    },
    "&.Mui-disabled svg": {
      fill: darkPalette.action.disabled
    }
  },
  lightButton: {
    "& svg": {
      fill: lightPalette.action.active
    },
    padding: "8px",
    transition: `background-color ${theme.transitions.common.short},
    box-shadow ${theme.transitions.common.short}`,
    "&:focus, &:hover": {
      backgroundColor: lightPalette.action.hover
    },
    "&:active, &.Mui-active": {
      backgroundColor: lightPalette.action.selected,
      boxShadow: `0 0 0 0.16rem rgba(255, 255, 255, 0.35)`
    },
    "&.Mui-disabled svg": {
      fill: lightPalette.action.disabled
    }
  },
  greenButton: {
    "& svg": {
      fill: theme.palette.primary.main
    },
    padding: "8px",
    transition: `background-color ${theme.transitions.common.short},
    box-shadow ${theme.transitions.common.short}`,
    "&:focus, &:hover": {
      backgroundColor: theme.palette.primary.hover
    },
    "&:active, &.Mui-active": {
      backgroundColor: theme.palette.primary.action,
      boxShadow: `0 0 0 0.16rem ${chroma(theme.palette.primary.main).alpha(
        0.35
      )}`
    },
    "&.Mui-disabled svg": {
      fill: darkPalette.action.disabled
    }
  },
  small: {
    "& svg": {
      height: "20px",
      width: "20px"
    },
    padding: "4px"
  }
});

IconButton.propTypes = Object.assign({}, MuiIconButton.propTypes, {
  green: PropTypes.bool,
  light: PropTypes.bool,
  small: PropTypes.bool
});

IconButton.defaultProps = Object.assign({}, MuiIconButton.defaultProps, {
  className: "",
  green: false,
  light: false,
  small: false
});

export default withStyles(styles)(IconButton);
